import styles from './BundleSummaryCard.scss';

import cx from 'classnames';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Bundle } from 'src/api/contentful/contentTypes.cloverdotcom';
import { BundleSummaryMoreContainer } from 'src/components/BundleSummaryMoreContainer/BundleSummaryMoreContainer';
import { ContentImage } from 'src/components/ContentImage/ContentImage';
import { useFeatureFlagsContext } from 'src/components/FeatureFlags/FeatureFlags';
import { TextV2 } from 'src/components/TextV2/TextV2';
import { useResizeObserver } from 'src/hooks/UseResizeObserver';
import { useMobile, useTablet } from 'src/hooks/UseResponsive';
import { BundlePricings } from 'src/hooks/useDCS';
import { FlatEntry } from 'src/services/cms/flattenContentful';
import { isBundleSelfServicePurchaseEnabled } from 'src/utils/BundlePricingUtils';
import { BundleCTA } from '../BundleCTA/BundleCTA';
import { BundleSummaryFeatures } from '../BundleSummaryFeatures/BundleSummaryFeatures';
import { BundleSummaryCardPrice } from './BundleSummaryCardPrice/BundleSummaryCardPrice';

export interface BundleSummaryCardProps extends React.HTMLAttributes<HTMLDivElement> {
  bundle?: FlatEntry<Bundle>;
  solutionTitle?: string;
  pricings?: BundlePricings;
  showMoreCTA?: string;
  showLessCTA?: string;
  tagText?: string;
  isWithSpaceLayout?: boolean;
  showAllCards?: boolean;
  hasHybridFeaturesLayout?: boolean;
  isBestSeller?: boolean;
  isPlusIconForShowMore?: boolean;
  isBundleCheckoutAtBottom?: boolean;
  isBundleFeaturesAlwaysOpen?: boolean;
  onResizeInnerHeight?: (bundleId: string, height: number) => void;
  onResizeHeadingHeight?: (bundleId: string, height: number) => void;
  onResizeFeaturesHeight?: (bundleId: string, height: number) => void;
}

export const BundleSummaryCard: React.FC<BundleSummaryCardProps> = ({
  bundle,
  solutionTitle,
  pricings,
  isWithSpaceLayout,
  className,
  showMoreCTA,
  showLessCTA,
  showAllCards,
  hasHybridFeaturesLayout,
  isBestSeller,
  tagText,
  isPlusIconForShowMore,
  isBundleCheckoutAtBottom,
  isBundleFeaturesAlwaysOpen,
  onResizeInnerHeight,
  onResizeHeadingHeight,
  onResizeFeaturesHeight,
}) => {
  if (!bundle) return null;

  const { title, description, bundleSummaryImage, shouldUseCheckoutLink, checkoutLink, id, dcsId } = bundle;

  const rootRef = useRef<HTMLElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLDivElement>(null);
  const featuresRef = useRef<HTMLDivElement>(null);
  const onResizeHeight = (prop: string, height: number, callback?: (bundleId: string, height: number) => void) => {
    if (rootRef.current) rootRef.current.style.setProperty(prop, `${height}px`);
    if (callback && id) callback(id, height);
  };

  useResizeObserver({ targetRef: innerRef, callback: ({ height }) => onResizeHeight('--my-inner-height', height, onResizeInnerHeight) });
  useResizeObserver({ targetRef: headingRef, callback: ({ height }) => onResizeHeight('--my-heading-height', height, onResizeHeadingHeight) });
  useResizeObserver({ targetRef: featuresRef, callback: ({ height }) => onResizeHeight('--my-features-height', height, onResizeFeaturesHeight) });

  const isTablet = useTablet();
  const isMobile = useMobile();
  const [showMoreFeatures, setShowMoreFeatures] = useState(false);
  const { isFlagEnabled } = useFeatureFlagsContext();
  const isDescriptionEnabled = isFlagEnabled('show-product-description-global');

  useEffect(() => {
    setShowMoreFeatures(false);
  }, [isTablet]);

  const isFeaturesAlwaysOpen = isBundleFeaturesAlwaysOpen && !isWithSpaceLayout;
  const isShowMoreVisible = isWithSpaceLayout ? !isMobile : !isFeaturesAlwaysOpen && isTablet;
  const isExpandUpAnimation = isWithSpaceLayout && showMoreFeatures;
  const isAltDesignWithSpace = isWithSpaceLayout && isPlusIconForShowMore && isBundleCheckoutAtBottom;
  const bundleFeaturesId = `bundle-features-${id}`;

  const bundleFeatures = useMemo(() => {
    const isFeaturesOpen = showMoreFeatures || showAllCards;
    const isExpandDownAnimation = !isWithSpaceLayout && isFeaturesOpen;
    const ariaHidden = isFeaturesAlwaysOpen ? false : !isFeaturesOpen || !isShowMoreVisible;
    const { title, bundleSummaryFeatureValues } = bundle;
    return (
      <div
        id={bundleFeaturesId}
        className={cx(styles.bundleSummaryCardFeatures, {
          [styles.bundleSummaryCardFeaturesShow]: isExpandUpAnimation || isExpandDownAnimation || isFeaturesAlwaysOpen,
          [styles.bundleSummaryCardFeaturesWithHeight]: isExpandDownAnimation || isFeaturesAlwaysOpen,
        })}
        aria-hidden={ariaHidden}
      >
        <div ref={featuresRef}>
          <BundleSummaryFeatures {...{ title, hasHybridFeaturesLayout, bundleSummaryFeatureValues }} />
        </div>
      </div>
    );
  }, [showMoreFeatures, showAllCards, isWithSpaceLayout, bundle, isExpandUpAnimation, bundleFeaturesId, hasHybridFeaturesLayout, isFeaturesAlwaysOpen]);

  const bundleCheckoutLink = useMemo(() => {
    const isSelfServicePurchaseEnabled = isBundleSelfServicePurchaseEnabled(pricings);

    return (
      <div className={styles.bundleSummaryCardBundleCta}>
        <BundleCTA
          checkoutLink={checkoutLink}
          dcsId={dcsId}
          isSelfServicePurchaseEnabled={isSelfServicePurchaseEnabled}
          shouldUseCheckoutLink={shouldUseCheckoutLink}
          solutionTitle={solutionTitle}
        />
      </div>
    );
  }, [isBundleCheckoutAtBottom, checkoutLink, pricings]);

  return (
    <section
      className={cx(styles.bundleSummaryCard, className, {
        [styles.bundleSummaryCardWithSpace]: isWithSpaceLayout,
        [styles.bundleSummaryCardWithoutSpace]: !isWithSpaceLayout,
        [styles.bundleSummaryCardAltWithSpace]: isAltDesignWithSpace,
        [styles.bundleSummaryCardAlwaysOpen]: isFeaturesAlwaysOpen,
        [styles.bundleSummaryCardBestSellerWithSpace]: isBestSeller && isWithSpaceLayout,
        [styles.bundleSummaryCardBestSellerWithoutSpace]: isBestSeller && !isWithSpaceLayout,
      })}
      ref={rootRef}
    >
      <div className={styles.bundleSummaryCardContainer}>
        {isBestSeller && (
          <div className={styles.bundleSummaryCardTagContainer}>
            <div className={styles.bundleSummaryCardTag}>
              <TextV2 tagName="p" value={tagText} variant="eyebrow" />
            </div>
          </div>
        )}
        <div className={styles.bundleSummaryCardContent}>
          <ContentImage
            entry={bundleSummaryImage}
            className={cx(styles.bundleSummaryCardImage, {
              [styles.bundleSummaryCardImageHide]: isExpandUpAnimation,
            })}
            aria-hidden={isExpandUpAnimation}
          />
          <div className={styles.bundleSummaryCardInnerContent} ref={innerRef}>
            <div className={cx(styles.bundleSummaryCardHeading, { [styles.bundleSummaryCardHeadingExpandUp]: isExpandUpAnimation })}>
              <div ref={headingRef}>
                <TextV2 tagName="h2" variant="eyebrow" value={title} entry={{ ...bundle, field: 'title' }} className={styles.bundleSummaryCardTitle} />
                <BundleSummaryCardPrice pricings={pricings} />
              </div>
            </div>
            {isDescriptionEnabled && (
              <TextV2
                isMarkdown
                value={description}
                entry={{ ...bundle, field: 'description' }}
                tagName="div"
                className={cx(styles.bundleSummaryCardDescription, { [styles.bundleSummaryCardDescriptionHide]: isExpandUpAnimation })}
                aria-hidden={isExpandUpAnimation}
              />
            )}
          </div>
          {!isWithSpaceLayout && bundleFeatures}
        </div>
        {isWithSpaceLayout ? !isBundleCheckoutAtBottom && bundleCheckoutLink : bundleCheckoutLink}
      </div>
      {isShowMoreVisible && (
        <BundleSummaryMoreContainer
          className={cx(styles.moreContainer, isPlusIconForShowMore && styles.moreContainerWithPlusIcon)}
          aria-controls={bundleFeaturesId}
          toggleMore={() => setShowMoreFeatures(bool => !bool)}
          {...{ showMoreFeatures, showMoreCTA, showLessCTA, isWithSpaceLayout, isPlusIconForShowMore }}
        />
      )}
      {isWithSpaceLayout && bundleFeatures}
      {isWithSpaceLayout && isBundleCheckoutAtBottom && bundleCheckoutLink}
    </section>
  );
};
